import React, { useRef,useState, useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tabs,
  Tab,
  Typography,
  Popper,
  Paper,
  ClickAwayListener,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { Close as CloseIcon, Circle as CircleIcon } from '@mui/icons-material';
import emojiData from '@emoji-mart/data';

const EMOJI_CATEGORIES = [
  { name: 'Smileys & Emotion', icon: '😀', key: 'people' },
  { name: 'People & Body', icon: '👋', key: 'people' },
  { name: 'Animals & Nature', icon: '🐻', key: 'nature' },
  { name: 'Food & Drink', icon: '🍔', key: 'foods' },
  { name: 'Travel & Places', icon: '🚗', key: 'places' },
  { name: 'Activities', icon: '⚽', key: 'activity' },
  { name: 'Objects', icon: '💡', key: 'objects' },
  { name: 'Symbols', icon: '💟', key: 'symbols' },
  { name: 'Flags', icon: '🏳️', key: 'flags' },
];

const SKIN_TONES = [
  { name: 'Default', modifier: '', icon: <CircleIcon sx={{ fontSize: 16 }} /> },
  { name: 'Light', modifier: '\u{1F3FB}', color: '#FFD9B3' },
  { name: 'Medium-Light', modifier: '\u{1F3FC}', color: '#E6B88A' },
  { name: 'Medium', modifier: '\u{1F3FD}', color: '#D4966E' },
  { name: 'Medium-Dark', modifier: '\u{1F3FE}', color: '#A67C52' },
  { name: 'Dark', modifier: '\u{1F3FF}', color: '#6B4423' },
];

const CustomEmojiPicker = ({ open, onClose, onEmojiSelect, anchorEl, position = 'bottom-start' }) => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [skinTone, setSkinTone] = useState(0);

  // Get emojis from emoji-mart data
  const getEmojisForCategory = useCallback((categoryIndex) => {
    const category = EMOJI_CATEGORIES[categoryIndex];
    if (!emojiData?.categories || !emojiData?.emojis) return [];

    // Find the category in the data
    const categoryData = emojiData.categories.find(cat => cat.id === category.key);
    if (!categoryData || !categoryData.emojis) return [];

    // Get emoji objects from the emoji IDs
    const emojis = categoryData.emojis
      .map(emojiId => emojiData.emojis[emojiId])
      .filter(Boolean)
      .slice(0, 200); // Limit for performance

    return emojis;
  }, []);

  const filteredEmojis = useMemo(() => {
    let emojis = getEmojisForCategory(activeCategory);
    
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      emojis = emojis.filter(emoji => 
        emoji.name?.toLowerCase().includes(search) ||
        emoji.keywords?.some(k => k.toLowerCase().includes(search))
      );
    }

    return emojis;
  }, [activeCategory, searchText, getEmojisForCategory]);

  const handleEmojiClick = (emoji) => {
    let emojiChar = emoji.skins?.[skinTone]?.native || emoji.skins?.[0]?.native || emoji.native;
    onEmojiSelect(emojiChar);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    onClose();
  };

  const handleClickAway = () => {
    onClose();
  };

  if (!open || !anchorEl) return null;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={position}
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: { altBoundary: true, fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'] },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: { altAxis: false, altBoundary: true, tether: false },
          },
        ]}
      >
        <Paper
          sx={{
            width: 500,
            maxWidth: '90vw',
            maxHeight: '70vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 3,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="Search emojis..."
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ flex: 1 }}
            />
            
            {/* Skin Tone Selector */}
            <ToggleButtonGroup
              value={skinTone}
              exclusive
              onChange={(e, newValue) => newValue !== null && setSkinTone(newValue)}
              size="small"
            >
              {SKIN_TONES.map((tone, index) => (
                <ToggleButton
                  key={index}
                  value={index}
                  sx={{
                    width: 32,
                    height: 32,
                    p: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {tone.color ? (
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: tone.color,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  ) : (
                    tone.icon
                  )}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Emoji Grid */}
          <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 1,
              }}
            >
              {filteredEmojis.map((emoji) => {
                const emojiChar = skinTone > 0 && emoji.skins?.[skinTone]
                  ? emoji.skins[skinTone].native
                  : emoji.skins?.[0]?.native || emoji.native;

                return (
                  <Box
                    key={emoji.id}
                    onClick={() => handleEmojiClick(emoji)}
                    sx={{
                      fontSize: '28px',
                      cursor: 'pointer',
                      textAlign: 'center',
                      p: 1,
                      borderRadius: 1,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'action.hover',
                        transform: 'scale(1.2)',
                      },
                    }}
                    title={emoji.name}
                  >
                    {emojiChar}
                  </Box>
                );
              })}
            </Box>
            
            {filteredEmojis.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No emojis found
                </Typography>
              </Box>
            )}
          </Box>

          {/* Category Tabs */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Tabs
              value={activeCategory}
              onChange={(e, newValue) => setActiveCategory(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 48,
                '& .MuiTab-root': {
                  minWidth: 60,
                  fontSize: '24px',
                  minHeight: 48,
                },
              }}
            >
              {EMOJI_CATEGORIES.map((category, index) => (
                <Tab key={index} label={category.icon} />
              ))}
            </Tabs>
          </Box>
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
};

export default CustomEmojiPicker;
